import {Box, Button, Grid} from "@mui/material";
import React, {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {TFunction} from "i18next";
import {ObjectSchema} from "yup";
import {AnyObject} from "yup/lib/object";
import {FormikValues, useFormik} from "formik";
import {useSnackbar} from "notistack";
import DefaultDialog, {DefaultDialogSize} from "./ClosableDialog";
import {UseModal} from "../../hooks/useModal";
import InputFactory from "../input/factory/InputFactory";
import {InputField} from "../input/factory/types";
import {ListRequest, ListResponseType} from "../../app/api/types";
import getCompleteFieldName from "../input/factory/helper";

export interface UpdateDialogProps<T, V extends ListResponseType, U extends ListRequest> {
    titleKey: string;
    inputFields: InputField<T, V, U>[];
    validationSchema?: (t: TFunction) => ObjectSchema<AnyObject>;
    initialValues: Partial<T>;
    entity?: T;
    modal: UseModal;
    onClose?: () => void;
    onSubmit: (payload: Partial<T>) => Promise<number> | Promise<void>;
    onSuccess?: (id?: number) => void;
    children?: React.ReactNode | React.ReactNode[];
    saveDisabled?: boolean;
    spacing?: number;
}

export default function UpdateDialog<T extends FormikValues, V extends ListResponseType, U extends ListRequest>({
    titleKey,
    entity,
    inputFields,
    validationSchema,
    initialValues,
    onClose,
    onSuccess,
    onSubmit,
    modal,
    children,
    saveDisabled,
    spacing = 4,
}: UpdateDialogProps<T, V, U>) {
    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();
    const form = useFormik<Partial<T>>({
        initialValues,
        validationSchema: validationSchema?.(t),
        validateOnChange: true,
        onSubmit: async (values) => {
            const requestData = {
                ...entity,
                ...values,
            };

            return onSubmit?.(requestData).then((id) => {
                enqueueSnackbar(t("generic_savedSuccessfully"), {variant: "success"});
                onSuccess?.(id ?? undefined);
                handleClose();
            });
        },
    });

    useEffect(() => {
        form.resetForm({values: initialValues});
    }, [JSON.stringify(initialValues)]);

    const handleClose = () => {
        form.resetForm({values: initialValues});
        onClose?.();
        modal.onClose();
    };
    return (
        <DefaultDialog
            size={DefaultDialogSize.LARGE}
            open={modal.isOpen}
            titleKey={titleKey}
            onClose={handleClose}
            actions={
                <Box>
                    <Button
                        color="primary"
                        variant="contained"
                        type="submit"
                        disabled={saveDisabled || !form.dirty || !form.isValid}
                        onClick={form.submitForm}
                    >
                        {t("action_save")}
                    </Button>
                </Box>
            }
        >
            {children}
            <Grid container spacing={spacing} sx={{marginTop: "0", marginBottom: "24px"}}>
                {inputFields.map((field: InputField<T, V, U>) => {
                    const name = getCompleteFieldName(field.fieldName, field.subFieldName);
                    return <InputFactory inputField={field} form={form} key={`updateField_${name}`} />;
                })}
            </Grid>
        </DefaultDialog>
    );
}
